/**
 * Autopilot Page - AI Trading Bot Platform
 *
 * Features:
 * - 3 Trading Strategies (Infinity Grid, Smart DCA, BTC Rebalancer)
 * - Real-time portfolio tracking
 * - Backtesting simulator
 * - Performance analytics
 * - Social trading feed
 */

import React, { useState, useMemo, useEffect } from 'react';
import { logger } from '../services/logger';
import { useToast } from '../components/Toast';
import { useConfirmation } from '../hooks/useConfirmation';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import {
  StrategyType,
  StrategyStatus,
  PositionStatus,
  type Strategy,
  type TradingPosition,
  type BacktestResult,
  type PortfolioSnapshot,
  type InfinityGridConfig,
  type SmartDCAConfig,
  type BTCRebalancerConfig,
} from '../types';
import {
  portfolioAPI,
  strategyAPI,
  positionAPI,
  healthAPI,
  exchangeAPI
} from '../services/tradingBotAPI';
import { BinanceConnectionModal } from '../components/BinanceConnectionModal';
import { WinRateChart } from '../components/visualizations/WinRateChart';
// React Query hooks
import {
  usePortfolio,
  useStrategies,
  usePositions,
  useBackendHealth,
  useCreateStrategy,
  useActivateStrategy,
  usePauseStrategy,
  useStopStrategy,
  useDeleteStrategy,
} from '../hooks/queries';

const Autopilot: React.FC = () => {
  // ============================================
  // State Management
  // ============================================

  // Toast and Confirmation hooks
  const toast = useToast();
  const { confirm, confirmationProps } = useConfirmation();

  // UI State (local)
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyType | null>(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showBinanceModal, setShowBinanceModal] = useState(false);
  const [binanceConnected, setBinanceConnected] = useState(false);
  const [binanceConnectionInfo, setBinanceConnectionInfo] = useState<any>(null);

  // React Query Data Fetching
  const { data: portfolio, isLoading: portfolioLoading, error: portfolioError } = usePortfolio();
  const { data: activeStrategies = [], isLoading: strategiesLoading } = useStrategies();
  const { data: positions = [], isLoading: positionsLoading } = usePositions();
  const { data: backendHealth } = useBackendHealth();

  // React Query Mutations
  const createStrategyMutation = useCreateStrategy();
  const activateStrategyMutation = useActivateStrategy();
  const pauseStrategyMutation = usePauseStrategy();
  const stopStrategyMutation = useStopStrategy();
  const deleteStrategyMutation = useDeleteStrategy();

  // Derived state
  const loading = portfolioLoading || strategiesLoading || positionsLoading;
  const backendOnline = backendHealth?.status === 'ok' || false;
  const error = portfolioError ? (portfolioError as Error).message : null;

  // ============================================
  // Mock Data
  // ============================================

  const mockPortfolio: PortfolioSnapshot = {
    timestamp: Date.now(),
    totalValueUSDT: 50000,
    availableBalance: 35000,
    lockedBalance: 15000,
    unrealizedPnl: 2450,
    realizedPnl: 5320,
    totalPnl: 7770,
    totalPnlPercent: 18.4,
    assets: {
      'BTC': 0.5,
      'ETH': 3.2,
      'SOL': 45,
      'USDT': 35000,
    }
  };

  const mockActivePositions: TradingPosition[] = [
    {
      id: '1',
      strategyId: 'inf-grid-1',
      symbol: 'BTC/USDT',
      side: 'long',
      entryPrice: 67800,
      quantity: 0.1,
      currentPrice: 68250,
      unrealizedPnl: 45,
      unrealizedPnlPercent: 0.66,
      status: PositionStatus.OPEN,
      exchange: 'binance',
      openedAt: Date.now() - 3600000,
    },
    {
      id: '2',
      strategyId: 'dca-1',
      symbol: 'ETH/USDT',
      side: 'long',
      entryPrice: 3420,
      quantity: 1.5,
      currentPrice: 3450,
      unrealizedPnl: 45,
      unrealizedPnlPercent: 0.88,
      status: PositionStatus.OPEN,
      exchange: 'binance',
      openedAt: Date.now() - 7200000,
    },
  ];

  // ============================================
  // Effects
  // ============================================

  // Check Binance connection status on mount
  // Note: Portfolio, strategies, and positions are now loaded automatically by React Query
  useEffect(() => {
    const checkBinanceStatus = async () => {
      try {
        const exchangeStatus = await exchangeAPI.getStatus();
        if (exchangeStatus.success && exchangeStatus.data?.connected) {
          setBinanceConnected(true);
          setBinanceConnectionInfo(exchangeStatus.data);
          logger.info('Autopilot', '✅ Binance connected', exchangeStatus.data);
        } else {
          setBinanceConnected(false);
          setBinanceConnectionInfo(null);
        }
      } catch (err) {
        logger.warn('Autopilot', '⚠️ Could not check Binance connection status');
        setBinanceConnected(false);
      }
    };

    checkBinanceStatus();
  }, []);

  // ============================================
  // Computed Values
  // ============================================

  const totalActiveStrategies = useMemo(() => {
    return activeStrategies.filter(s => s.status === StrategyStatus.ACTIVE).length;
  }, [activeStrategies]);

  const todayPL = useMemo(() => {
    // Calculate from portfolio data or fallback to mock
    return portfolio?.totalPnl ? portfolio.totalPnl * 0.1 : 345.50;
  }, [portfolio]);

  // ============================================
  // Handlers
  // ============================================

  const handleSelectStrategy = (type: StrategyType) => {
    setSelectedStrategy(type);
    setShowConfigModal(true);
  };

  const handleCreateStrategy = (config: any) => {
    if (!selectedStrategy) return;

    createStrategyMutation.mutate({
      name: `${selectedStrategy} Strategy`,
      type: selectedStrategy,
      config,
      description: `Auto-created ${selectedStrategy} trading strategy`,
    }, {
      onSuccess: (data) => {
        setShowConfigModal(false);
        setSelectedStrategy(null);
        logger.info('Autopilot', '✅ Strategy created successfully', data);
        toast('Stratégie créée avec succès !', 'success');
      },
      onError: (err: Error) => {
        logger.error('Autopilot', '❌ Failed to create strategy', err);
        toast(`Erreur lors de la création: ${err.message}`, 'error');
      },
    });
  };

  const handleActivateStrategy = (id: string) => {
    activateStrategyMutation.mutate(id, {
      onSuccess: () => {
        logger.info('Autopilot', '✅ Strategy activated');
        toast('Stratégie activée !', 'success');
      },
      onError: (err: Error) => {
        logger.error('Autopilot', '❌ Failed to activate strategy', err);
        toast(`Erreur lors de l'activation: ${err.message}`, 'error');
      },
    });
  };

  const handlePauseStrategy = (id: string) => {
    pauseStrategyMutation.mutate(id, {
      onSuccess: () => {
        logger.info('Autopilot', '✅ Strategy paused');
        toast('Stratégie mise en pause !', 'success');
      },
      onError: (err: Error) => {
        logger.error('Autopilot', '❌ Failed to pause strategy', err);
        toast(`Erreur lors de la mise en pause: ${err.message}`, 'error');
      },
    });
  };

  const handleStopStrategy = (id: string) => {
    stopStrategyMutation.mutate(id, {
      onSuccess: () => {
        logger.info('Autopilot', '✅ Strategy stopped');
        toast('Stratégie arrêtée !', 'success');
      },
      onError: (err: Error) => {
        logger.error('Autopilot', '❌ Failed to stop strategy', err);
        toast(`Erreur lors de l'arrêt: ${err.message}`, 'error');
      },
    });
  };

  const handleDeleteStrategy = async (id: string) => {
    const confirmed = await confirm({
      title: 'Supprimer la stratégie',
      message: 'Êtes-vous sûr de vouloir supprimer cette stratégie ?',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      variant: 'danger'
    });

    if (!confirmed) return;

    deleteStrategyMutation.mutate(id, {
      onSuccess: () => {
        logger.info('Autopilot', '✅ Strategy deleted');
        toast('Stratégie supprimée !', 'success');
      },
      onError: (err: Error) => {
        logger.error('Autopilot', '❌ Failed to delete strategy', err);
        toast(`Erreur lors de la suppression: ${err.message}`, 'error');
      },
    });
  };

  const handleBinanceConnectionSuccess = async () => {
    // Refresh Binance connection status after successful connection
    try {
      const exchangeStatus = await exchangeAPI.getStatus();
      if (exchangeStatus.success && exchangeStatus.data?.connected) {
        setBinanceConnected(true);
        setBinanceConnectionInfo(exchangeStatus.data);
        logger.info('Autopilot', '✅ Binance connection updated', exchangeStatus.data);
      }
    } catch (err) {
      logger.error('Autopilot', '❌ Failed to refresh Binance status', err);
    }
  };

  const handleDisconnectBinance = async () => {
    const confirmed = await confirm({
      title: 'Déconnecter Binance',
      message: 'Êtes-vous sûr de vouloir déconnecter votre compte Binance ?',
      confirmText: 'Déconnecter',
      cancelText: 'Annuler',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      const response = await exchangeAPI.disconnect();
      if (response.success) {
        setBinanceConnected(false);
        setBinanceConnectionInfo(null);
        toast('Compte Binance déconnecté', 'success');
      }
    } catch (err: any) {
      logger.error('Autopilot', '❌ Failed to disconnect Binance', err);
      toast(`Erreur: ${err.message}`, 'error');
    }
  };

  // ============================================
  // Render
  // ============================================

  // Loading State
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
        padding: '2rem',
        color: '#fff',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '5px solid rgba(59, 130, 246, 0.3)',
            borderTop: '5px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem',
          }}></div>
          <p style={{ fontSize: '1.2rem', opacity: 0.8 }}>Chargement des données...</p>
          <p style={{ fontSize: '0.9rem', opacity: 0.6 }}>Connexion au backend...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)',
      padding: '2rem',
      color: '#fff',
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* Backend Status Banner */}
        {!backendOnline && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0, color: '#ef4444', fontWeight: '600' }}>
              ⚠️ Backend Offline - Utilisation des données de démonstration
            </p>
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
              Le serveur backend n'est pas accessible. Démarrez-le avec: <code>cd trading-bot-backend && npm run dev</code>
            </p>
          </div>
        )}

        {backendOnline && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
            textAlign: 'center',
          }}>
            <p style={{ margin: 0, color: '#10b981', fontWeight: '600' }}>
              ✅ Backend Online - Données en temps réel
            </p>
          </div>
        )}

        {/* Binance Connection Status */}
        {backendOnline && !binanceConnected && (
          <div style={{
            background: 'rgba(251, 191, 36, 0.1)',
            border: '1px solid rgba(251, 191, 36, 0.3)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <p style={{ margin: 0, color: '#fbbf24', fontWeight: '600' }}>
                🔗 Connectez votre compte Binance pour commencer à trader
              </p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
                Utilisez le testnet Binance (gratuit, sans argent réel) pour tester les stratégies
              </p>
            </div>
            <button
              onClick={() => setShowBinanceModal(true)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#fbbf24',
                color: '#1a1a2e',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                marginLeft: '1rem',
              }}
            >
              Connecter Binance
            </button>
          </div>
        )}

        {backendOnline && binanceConnected && binanceConnectionInfo && (
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <p style={{ margin: 0, color: '#10b981', fontWeight: '600' }}>
                ✅ Binance connecté - {binanceConnectionInfo.label || 'Mon compte Binance'}
              </p>
              <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', opacity: 0.8 }}>
                {binanceConnectionInfo.testnet ? '🧪 Mode Testnet' : '⚠️ Mode Production'} •
                Connecté le {new Date(binanceConnectionInfo.connectedAt).toLocaleDateString('fr-FR')}
              </p>
            </div>
            <button
              onClick={handleDisconnectBinance}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(239, 68, 68, 0.2)',
                color: '#ef4444',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                marginLeft: '1rem',
              }}
            >
              Déconnecter
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
          }}>
            <p style={{ margin: 0, color: '#ef4444' }}>❌ Erreur: {error}</p>
          </div>
        )}

        {/* ============================================ */}
        {/* Hero Section */}
        {/* ============================================ */}

        <div style={{
          textAlign: 'center',
          marginBottom: '3rem',
          paddingBottom: '2rem',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div
            role="status"
            aria-live="polite"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'rgba(245, 158, 11, 0.12)',
              border: '1px solid rgba(245, 158, 11, 0.5)',
              borderRadius: '12px',
              padding: '0.75rem 1.25rem',
              marginBottom: '1.25rem',
              fontSize: '0.9rem',
              fontWeight: 600,
              color: '#f59e0b',
              maxWidth: '720px',
              margin: '0 auto 1.25rem',
              textAlign: 'left',
            }}
          >
            <span style={{ fontSize: '1.25rem', flexShrink: 0 }} aria-hidden="true">⚠️</span>
            <span>
              <strong>Mode démo — Paper Trading.</strong> Aucune transaction réelle, aucun fonds engagé.
              Les exchanges ne sont pas encore connectés. L'exécution réelle arrive en Phase 3 de la roadmap.
            </span>
          </div>

          <h1 style={{
            fontSize: '3.5rem',
            fontWeight: '800',
            marginBottom: '1rem',
            background: 'linear-gradient(90deg, #60a5fa 0%, #c084fc 50%, #f472b6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            lineHeight: '1.2',
          }}>
            ✈️ Autopilot
          </h1>

          <p style={{
            fontSize: '1.2rem',
            opacity: 0.8,
            maxWidth: '700px',
            margin: '0 auto 2rem',
            lineHeight: '1.6',
          }}>
            AI-Powered Trading Strategies • Automated Execution • Risk Management
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap',
            fontSize: '0.9rem',
          }}>
            <div style={{
              background: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(10px)',
              padding: '0.75rem 1.25rem',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              🎯 Win Rate: <strong>68.5%</strong>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(10px)',
              padding: '0.75rem 1.25rem',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              📈 Avg Return: <strong>+12.3%</strong>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(10px)',
              padding: '0.75rem 1.25rem',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              ⚡ Active Positions: <strong>{mockActivePositions.length}</strong>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* Quick Stats - 4 KPI Cards */}
        {/* ============================================ */}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '1.5rem',
          marginBottom: '3rem',
        }}>

          {/* Portfolio Value */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '1.5rem',
            border: '1px solid rgba(255,255,255,0.1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              marginBottom: '1rem',
            }}>
              <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>Total Portfolio</span>
              <span style={{
                background: 'rgba(16, 185, 129, 0.2)',
                color: '#10b981',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>+{mockPortfolio.totalPnlPercent}%</span>
            </div>

            <div style={{
              fontSize: '2.25rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
            }}>
              ${mockPortfolio.totalValueUSDT.toLocaleString()}
            </div>

            <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>
              Available: ${mockPortfolio.availableBalance.toLocaleString()} •
              Locked: ${mockPortfolio.lockedBalance.toLocaleString()}
            </div>
          </div>

          {/* Active Strategies */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '1.5rem',
            border: '1px solid rgba(255,255,255,0.1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              marginBottom: '1rem',
            }}>
              <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>Active Strategies</span>
              <span style={{
                background: 'rgba(59, 130, 246, 0.2)',
                color: '#3b82f6',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>Live</span>
            </div>

            <div style={{
              fontSize: '2.25rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
            }}>
              {totalActiveStrategies}
            </div>

            <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>
              {mockActivePositions.length} open positions • 0 pending orders
            </div>
          </div>

          {/* Total P/L */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '1.5rem',
            border: '1px solid rgba(255,255,255,0.1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              marginBottom: '1rem',
            }}>
              <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>Total P/L</span>
              <span style={{
                background: 'rgba(16, 185, 129, 0.2)',
                color: '#10b981',
                padding: '0.25rem 0.5rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>All-Time</span>
            </div>

            <div style={{
              fontSize: '2.25rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              color: mockPortfolio.totalPnl > 0 ? '#10b981' : '#ef4444',
            }}>
              {mockPortfolio.totalPnl > 0 ? '+' : ''}${mockPortfolio.totalPnl.toLocaleString()}
            </div>

            <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>
              Realized: ${mockPortfolio.realizedPnl.toLocaleString()} •
              Unrealized: ${mockPortfolio.unrealizedPnl.toLocaleString()}
            </div>
          </div>

          {/* Today's P/L */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '1.5rem',
            border: '1px solid rgba(255,255,255,0.1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0,0,0,0.2)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              marginBottom: '1rem',
            }}>
              <span style={{ opacity: 0.7, fontSize: '0.9rem' }}>Today's P/L</span>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 8px #10b981',
                animation: 'pulse 2s infinite',
              }} />
            </div>

            <div style={{
              fontSize: '2.25rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              color: todayPL > 0 ? '#10b981' : '#ef4444',
            }}>
              {todayPL > 0 ? '+' : ''}${todayPL.toLocaleString()}
            </div>

            <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>
              {((todayPL / mockPortfolio.totalValueUSDT) * 100).toFixed(2)}% of portfolio
            </div>
          </div>

        </div>

        {/* ============================================ */}
        {/* Strategy Selection Cards */}
        {/* ============================================ */}

        <div style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', fontWeight: '700' }}>
              🚀 Select Your Trading Strategy
            </h2>
            <p style={{ opacity: 0.7, fontSize: '1rem' }}>
              Choose from 3 proven strategies designed for different market conditions
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '1.5rem',
          }}>

            {/* Infinity Grid Strategy */}
            <div
              onClick={() => handleSelectStrategy(StrategyType.INFINITY_GRID)}
              style={{
                background: selectedStrategy === StrategyType.INFINITY_GRID
                  ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)'
                  : 'rgba(255,255,255,0.04)',
                borderRadius: '16px',
                padding: '2rem',
                border: selectedStrategy === StrategyType.INFINITY_GRID
                  ? '2px solid rgba(59, 130, 246, 0.5)'
                  : '2px solid rgba(255,255,255,0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (selectedStrategy !== StrategyType.INFINITY_GRID) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
                }
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                if (selectedStrategy !== StrategyType.INFINITY_GRID) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                }
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {selectedStrategy === StrategyType.INFINITY_GRID && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: '#3b82f6',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#fff',
                }}>✓</div>
              )}

              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>

              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                Infinity Grid
              </h3>

              <p style={{ opacity: 0.8, fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                Buy the dip automatically with increasing position sizes.
                Perfect for accumulating assets during volatile markets.
              </p>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Base Amount:</span>
                  <span style={{ fontWeight: '600' }}>$100</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Take Profit:</span>
                  <span style={{ fontWeight: '600', color: '#10b981' }}>+5%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Max Positions:</span>
                  <span style={{ fontWeight: '600' }}>5</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Risk Level:</span>
                  <span style={{ fontWeight: '600', color: '#f59e0b' }}>Medium</span>
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginBottom: '1rem',
              }}>
                <span style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>Volatility Friendly</span>
                <span style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#3b82f6',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>Auto DCA</span>
                <span style={{
                  background: 'rgba(168, 85, 247, 0.2)',
                  color: '#a855f7',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>Trailing Stop</span>
              </div>

              <div style={{
                opacity: 0.6,
                fontSize: '0.8rem',
                fontStyle: 'italic',
              }}>
                Best for: Bear markets, high volatility
              </div>
            </div>

            {/* Smart DCA Strategy */}
            <div
              onClick={() => handleSelectStrategy(StrategyType.SMART_DCA)}
              style={{
                background: selectedStrategy === StrategyType.SMART_DCA
                  ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
                  : 'rgba(255,255,255,0.04)',
                borderRadius: '16px',
                padding: '2rem',
                border: selectedStrategy === StrategyType.SMART_DCA
                  ? '2px solid rgba(16, 185, 129, 0.5)'
                  : '2px solid rgba(255,255,255,0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (selectedStrategy !== StrategyType.SMART_DCA) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                }
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                if (selectedStrategy !== StrategyType.SMART_DCA) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                }
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {selectedStrategy === StrategyType.SMART_DCA && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: '#10b981',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#fff',
                }}>✓</div>
              )}

              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💰</div>

              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                Smart DCA
              </h3>

              <p style={{ opacity: 0.8, fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                Intelligent Dollar Cost Averaging with reserve funds for extreme dips.
                Enhanced with optional MA200 boost for value zone buying. Pauses during overbought conditions (RSI &gt; 75).
              </p>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Base Amount:</span>
                  <span style={{ fontWeight: '600' }}>$50</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Frequency:</span>
                  <span style={{ fontWeight: '600' }}>Weekly</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Reserve Fund:</span>
                  <span style={{ fontWeight: '600' }}>30%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Risk Level:</span>
                  <span style={{ fontWeight: '600', color: '#10b981' }}>Low</span>
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginBottom: '1rem',
              }}>
                <span style={{
                  background: 'rgba(16, 185, 129, 0.2)',
                  color: '#10b981',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>RSI Based</span>
                <span style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#3b82f6',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>Scheduled</span>
                <span style={{
                  background: 'rgba(245, 158, 11, 0.2)',
                  color: '#f59e0b',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>Dip Hunter</span>
              </div>

              <div style={{
                opacity: 0.6,
                fontSize: '0.8rem',
                fontStyle: 'italic',
              }}>
                Best for: Long-term accumulation, beginners
              </div>
            </div>

            {/* BTC Rebalancer Strategy */}
            <div
              onClick={() => handleSelectStrategy(StrategyType.BTC_REBALANCER)}
              style={{
                background: selectedStrategy === StrategyType.BTC_REBALANCER
                  ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.05) 100%)'
                  : 'rgba(255,255,255,0.04)',
                borderRadius: '16px',
                padding: '2rem',
                border: selectedStrategy === StrategyType.BTC_REBALANCER
                  ? '2px solid rgba(245, 158, 11, 0.5)'
                  : '2px solid rgba(255,255,255,0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (selectedStrategy !== StrategyType.BTC_REBALANCER) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                  e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.3)';
                }
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={(e) => {
                if (selectedStrategy !== StrategyType.BTC_REBALANCER) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                }
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {selectedStrategy === StrategyType.BTC_REBALANCER && (
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: '#f59e0b',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#fff',
                }}>✓</div>
              )}

              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>₿</div>

              <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem' }}>
                BTC Rebalancer
              </h3>

              <p style={{ opacity: 0.8, fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                Automatically rebalance between ALT and BTC to accumulate more Bitcoin.
                Sell ALT when pumping, buy when BTC outperforms.
              </p>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1rem',
                marginBottom: '1rem',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Target Ratio:</span>
                  <span style={{ fontWeight: '600' }}>50% ALT / 50% BTC</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Threshold:</span>
                  <span style={{ fontWeight: '600' }}>+15% / -10%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Min Profit:</span>
                  <span style={{ fontWeight: '600', color: '#10b981' }}>2%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ opacity: 0.7 }}>Risk Level:</span>
                  <span style={{ fontWeight: '600', color: '#10b981' }}>Low</span>
                </div>
              </div>

              <div style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginBottom: '1rem',
              }}>
                <span style={{
                  background: 'rgba(245, 158, 11, 0.2)',
                  color: '#f59e0b',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>BTC Accumulator</span>
                <span style={{
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#3b82f6',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>Auto Rebalance</span>
                <span style={{
                  background: 'rgba(168, 85, 247, 0.2)',
                  color: '#a855f7',
                  padding: '0.35rem 0.75rem',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                }}>Fee Optimized</span>
              </div>

              <div style={{
                opacity: 0.6,
                fontSize: '0.8rem',
                fontStyle: 'italic',
              }}>
                Best for: ALT/BTC pairs, long-term BTC holders
              </div>
            </div>

          {/* MA200 DCA Strategy */}
          <div
            onClick={() => handleSelectStrategy(StrategyType.MA200_DCA)}
            style={{
              background: selectedStrategy === StrategyType.MA200_DCA
                ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)'
                : 'rgba(255,255,255,0.04)',
              borderRadius: '16px',
              padding: '2rem',
              border: selectedStrategy === StrategyType.MA200_DCA
                ? '2px solid rgba(139, 92, 246, 0.5)'
                : '2px solid rgba(255,255,255,0.1)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              if (selectedStrategy !== StrategyType.MA200_DCA) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.borderColor = 'rgba(139, 92, 246, 0.3)';
              }
              e.currentTarget.style.transform = 'translateY(-4px)';
            }}
            onMouseLeave={(e) => {
              if (selectedStrategy !== StrategyType.MA200_DCA) {
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
              }
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {selectedStrategy === StrategyType.MA200_DCA && (
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#8b5cf6',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                fontWeight: '700',
                color: '#fff',
              }}>✓</div>
            )}

            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📈</div>

            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem' }}>
              MA200 DCA
            </h3>

            <p style={{ opacity: 0.8, fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Value investing with 200-period moving average. Buy ONLY when price is near MA200.
              Perfect for accumulating at value zones.
            </p>

            <div style={{
              background: 'rgba(255,255,255,0.05)',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1rem',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                <span style={{ opacity: 0.7 }}>MA200 Zone:</span>
                <span style={{ fontWeight: '600' }}>±5%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                <span style={{ opacity: 0.7 }}>Buy Amount:</span>
                <span style={{ fontWeight: '600' }}>$50 - $1000</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.85rem' }}>
                <span style={{ opacity: 0.7 }}>Below MA200 Boost:</span>
                <span style={{ fontWeight: '600', color: '#8b5cf6' }}>+50%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span style={{ opacity: 0.7 }}>Risk Level:</span>
                <span style={{ fontWeight: '600', color: '#10b981' }}>Low</span>
              </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              marginBottom: '1rem',
            }}>
              <span style={{
                background: 'rgba(139, 92, 246, 0.2)',
                color: '#8b5cf6',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>Value Investing</span>
              <span style={{
                background: 'rgba(16, 185, 129, 0.2)',
                color: '#10b981',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>MA200 Based</span>
              <span style={{
                background: 'rgba(59, 130, 246, 0.2)',
                color: '#3b82f6',
                padding: '0.35rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>Patient Entry</span>
            </div>

            <div style={{
              opacity: 0.6,
              fontSize: '0.8rem',
              fontStyle: 'italic',
            }}>
              Best for: Long-term value accumulation, patient investors
            </div>
          </div>

          </div>

          {/* Configure Button */}
          {selectedStrategy && (
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <button
                onClick={() => setShowConfigModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  color: '#fff',
                  border: 'none',
                  padding: '1rem 2.5rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.4)';
                }}
              >
                ⚙️ Configure Strategy
              </button>
              <p style={{ opacity: 0.6, fontSize: '0.85rem', marginTop: '1rem' }}>
                Configure parameters and start paper trading (Phase B.4)
              </p>
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* Active Positions Table - Phase B.6 */}
        {/* ============================================ */}

        <div style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.1)',
          marginTop: '3rem',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem',
          }}>
            <div>
              <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', fontWeight: '700' }}>
                📊 Active Positions
              </h2>
              <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                {mockActivePositions.length} open positions across all strategies
              </p>
            </div>
            <button
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '10px',
                padding: '0.75rem 1.5rem',
                color: '#ef4444',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
              }}
            >
              Close All Positions
            </button>
          </div>

          {/* Table - Desktop */}
          <div style={{
            display: 'none',
            '@media (minWidth: 768px)': { display: 'block' }
          }}>
            <div style={{
              overflowX: 'auto',
              borderRadius: '12px',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
              }}>
                <thead>
                  <tr style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                  }}>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.7,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>Strategy</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.7,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>Symbol</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.7,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>Entry</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.7,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>Current</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.7,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>Quantity</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'right',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.7,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>P/L</th>
                    <th style={{
                      padding: '1rem',
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.7,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {mockActivePositions.map((position) => (
                    <tr key={position.id} style={{
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      transition: 'background 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{
                          display: 'inline-block',
                          background: 'rgba(59, 130, 246, 0.2)',
                          color: '#3b82f6',
                          padding: '0.35rem 0.75rem',
                          borderRadius: '8px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                        }}>
                          {position.strategyId.includes('inf-grid') ? 'Infinity Grid' :
                           position.strategyId.includes('dca') ? 'Smart DCA' : 'BTC Rebalancer'}
                        </div>
                      </td>
                      <td style={{ padding: '1rem', fontWeight: '600' }}>{position.symbol}</td>
                      <td style={{ padding: '1rem', opacity: 0.8 }}>${position.entryPrice.toLocaleString()}</td>
                      <td style={{ padding: '1rem', fontWeight: '600' }}>${position.currentPrice.toLocaleString()}</td>
                      <td style={{ padding: '1rem', opacity: 0.8 }}>{position.quantity}</td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>
                        <div style={{
                          display: 'inline-block',
                          color: position.unrealizedPnl > 0 ? '#10b981' : '#ef4444',
                          fontWeight: '600',
                        }}>
                          {position.unrealizedPnl > 0 ? '+' : ''}${position.unrealizedPnl.toFixed(2)}
                          <span style={{ opacity: 0.7, marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                            ({position.unrealizedPnl > 0 ? '+' : ''}{position.unrealizedPnlPercent.toFixed(2)}%)
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <button
                          onClick={() => toast.info(`Close position ${position.id} (Phase B.6 - UI Only)`)}
                          style={{
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            color: '#ef4444',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                          }}
                        >
                          Close
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cards - Mobile Responsive */}
          <div style={{ display: 'block' }}>
            {mockActivePositions.map((position) => (
              <div
                key={position.id}
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  border: '1px solid rgba(255,255,255,0.1)',
                  marginBottom: '1rem',
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: '1rem',
                }}>
                  <div>
                    <div style={{
                      display: 'inline-block',
                      background: 'rgba(59, 130, 246, 0.2)',
                      color: '#3b82f6',
                      padding: '0.35rem 0.75rem',
                      borderRadius: '8px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      marginBottom: '0.5rem',
                    }}>
                      {position.strategyId.includes('inf-grid') ? 'Infinity Grid' :
                       position.strategyId.includes('dca') ? 'Smart DCA' : 'BTC Rebalancer'}
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{position.symbol}</div>
                  </div>
                  <div style={{
                    color: position.unrealizedPnl > 0 ? '#10b981' : '#ef4444',
                    fontWeight: '700',
                    fontSize: '1.1rem',
                    textAlign: 'right',
                  }}>
                    {position.unrealizedPnl > 0 ? '+' : ''}${position.unrealizedPnl.toFixed(2)}
                    <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                      ({position.unrealizedPnl > 0 ? '+' : ''}{position.unrealizedPnlPercent.toFixed(2)}%)
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                  fontSize: '0.9rem',
                }}>
                  <div>
                    <div style={{ opacity: 0.6, marginBottom: '0.25rem' }}>Entry Price</div>
                    <div style={{ fontWeight: '600' }}>${position.entryPrice.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ opacity: 0.6, marginBottom: '0.25rem' }}>Current Price</div>
                    <div style={{ fontWeight: '600' }}>${position.currentPrice.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{ opacity: 0.6, marginBottom: '0.25rem' }}>Quantity</div>
                    <div style={{ fontWeight: '600' }}>{position.quantity}</div>
                  </div>
                  <div>
                    <div style={{ opacity: 0.6, marginBottom: '0.25rem' }}>Exchange</div>
                    <div style={{ fontWeight: '600', textTransform: 'capitalize' }}>{position.exchange}</div>
                  </div>
                </div>

                <button
                  onClick={() => toast.info(`Close position ${position.id} (Phase B.6 - UI Only)`)}
                  style={{
                    width: '100%',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '8px',
                    padding: '0.75rem',
                    color: '#ef4444',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                >
                  Close Position
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* ============================================ */}
        {/* Backtesting Simulator - Phase B.7 */}
        {/* ============================================ */}

        <div style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.1)',
          marginTop: '3rem',
        }}>
          <div style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', fontWeight: '700' }}>
              🔬 Strategy Backtesting
            </h2>
            <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>
              Test your strategies with historical data before deploying them live
            </p>
          </div>

          {/* Backtesting Configuration */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1.5rem',
            marginBottom: '2rem',
          }}>
            {/* Strategy Selection */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                opacity: 0.9,
              }}>
                Strategy
              </label>
              <select
                aria-label="Strategy"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                <option value={StrategyType.INFINITY_GRID}>Infinity Grid</option>
                <option value={StrategyType.SMART_DCA}>Smart DCA</option>
                <option value={StrategyType.MA200_DCA}>MA200 DCA</option>
                <option value={StrategyType.BTC_REBALANCER}>BTC Rebalancer</option>
              </select>
            </div>

            {/* Initial Capital */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                opacity: 0.9,
              }}>
                Initial Capital (USDT)
              </label>
              <input
                type="number"
                defaultValue={10000}
                min={1000}
                max={100000}
                step={1000}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                }}
              />
            </div>

            {/* Date Range */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                opacity: 0.9,
              }}>
                Backtest Period
              </label>
              <select
                aria-label="Backtest Period"
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  cursor: 'pointer',
                }}
              >
                <option value="1m">Last 1 Month</option>
                <option value="3m">Last 3 Months</option>
                <option value="6m">Last 6 Months</option>
                <option value="1y">Last 1 Year</option>
                <option value="2y">Last 2 Years</option>
              </select>
            </div>

            {/* Run Button */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
            }}>
              <button
                onClick={() => toast.info('Running backtest... (Phase B.7 - UI Only)')}
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #8b5cf6 0%, #c084fc 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
                }}
              >
                🚀 Run Backtest
              </button>
            </div>
          </div>

          {/* Backtest Results (Mock Data) */}
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            borderRadius: '12px',
            padding: '2rem',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}>
              <span>📈 Backtest Results</span>
              <span style={{
                background: 'rgba(16, 185, 129, 0.2)',
                color: '#10b981',
                padding: '0.25rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '600',
              }}>Sample Data</span>
            </h3>

            {/* KPI Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem',
            }}>
              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1.25rem',
              }}>
                <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Total Return</div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  color: '#10b981',
                }}>
                  +42.5%
                </div>
                <div style={{ opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  $4,250 profit
                </div>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1.25rem',
              }}>
                <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Win Rate</div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  color: '#3b82f6',
                }}>
                  68.5%
                </div>
                <div style={{ opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  123 wins / 57 losses
                </div>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1.25rem',
              }}>
                <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Max Drawdown</div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  color: '#f59e0b',
                }}>
                  -12.3%
                </div>
                <div style={{ opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Within safe limits
                </div>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1.25rem',
              }}>
                <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Sharpe Ratio</div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  color: '#8b5cf6',
                }}>
                  2.34
                </div>
                <div style={{ opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Excellent
                </div>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1.25rem',
              }}>
                <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Total Trades</div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                }}>
                  180
                </div>
                <div style={{ opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Avg: 6 trades/day
                </div>
              </div>

              <div style={{
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '10px',
                padding: '1.25rem',
              }}>
                <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Avg Trade</div>
                <div style={{
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  color: '#10b981',
                }}>
                  +$23.61
                </div>
                <div style={{ opacity: 0.6, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  Per winning trade
                </div>
              </div>
            </div>

            {/* Simple Equity Curve Visualization */}
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '10px',
              padding: '1.5rem',
              marginTop: '1.5rem',
            }}>
              <div style={{
                opacity: 0.7,
                fontSize: '0.85rem',
                marginBottom: '1rem',
                fontWeight: '600',
              }}>
                Equity Curve
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '4px',
                height: '120px',
              }}>
                {/* Simple bar chart simulation */}
                {[65, 70, 68, 75, 72, 78, 80, 76, 82, 85, 88, 84, 90, 93, 91, 95, 98, 96, 100, 103, 105, 102, 108, 110, 112, 115, 113, 118, 120, 125].map((value, idx) => (
                  <div
                    key={idx}
                    style={{
                      flex: 1,
                      background: `linear-gradient(to top, #10b981 0%, #059669 100%)`,
                      borderRadius: '2px',
                      height: `${value}%`,
                      opacity: 0.7,
                      transition: 'opacity 0.2s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '0.7';
                    }}
                  />
                ))}
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: '0.75rem',
                fontSize: '0.75rem',
                opacity: 0.5,
              }}>
                <span>Start</span>
                <span>+42.5%</span>
                <span>End</span>
              </div>
            </div>

            {/* Deploy Strategy Button */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginTop: '2rem',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={() => toast.info('Deploying strategy... (Phase B.7 - UI Only)')}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '1rem 2rem',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                }}
              >
                ✅ Deploy This Strategy Live
              </button>
              <button
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '1rem 2rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                }}
              >
                📊 View Detailed Report
              </button>
            </div>
          </div>
        </div>

        {/* ============================================ */}
        {/* Performance Analytics - Phase B.8 */}
        {/* ============================================ */}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '2rem',
          marginTop: '3rem',
        }}>

          {/* P/L Over Time Chart */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '2rem',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              📈 P/L Over Time
            </h3>

            <div style={{
              background: 'rgba(255,255,255,0.03)',
              borderRadius: '10px',
              padding: '1.5rem',
            }}>
              {/* Chart */}
              <div style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '3px',
                height: '180px',
                marginBottom: '1rem',
              }}>
                {[20, 25, 22, 30, 28, 35, 32, 38, 42, 40, 45, 48, 44, 50, 53, 51, 56, 60, 58, 62, 65, 63, 68, 70, 68, 72, 75, 73, 78, 80].map((value, idx) => {
                  const isProfit = value > 50;
                  return (
                    <div
                      key={idx}
                      style={{
                        flex: 1,
                        background: isProfit
                          ? 'linear-gradient(to top, #10b981 0%, #059669 100%)'
                          : 'linear-gradient(to top, #ef4444 0%, #dc2626 100%)',
                        borderRadius: '2px',
                        height: `${value}%`,
                        opacity: 0.7,
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                    />
                  );
                })}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '0.75rem',
                opacity: 0.5,
              }}>
                <span>30 days ago</span>
                <span>Today</span>
              </div>
            </div>

            {/* Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginTop: '1.5rem',
            }}>
              <div>
                <div style={{ opacity: 0.6, fontSize: '0.85rem', marginBottom: '0.25rem' }}>Best Day</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>+$450</div>
              </div>
              <div>
                <div style={{ opacity: 0.6, fontSize: '0.85rem', marginBottom: '0.25rem' }}>Worst Day</div>
                <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#ef4444' }}>-$120</div>
              </div>
            </div>
          </div>

          {/* Win Rate by Strategy */}
          <div style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '2rem',
            border: '1px solid rgba(255,255,255,0.1)',
          }}>
            <h3 style={{
              fontSize: '1.3rem',
              fontWeight: '700',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              🎯 Win Rate by Strategy
            </h3>

            {/* Strategy Performance Bars */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

              {/* Infinity Grid */}
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                }}>
                  <span>Infinity Grid</span>
                  <span style={{ fontWeight: '700', color: '#3b82f6' }}>72%</span>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  height: '12px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                    height: '100%',
                    width: '72%',
                    borderRadius: '8px',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.25rem' }}>
                  85 wins / 33 losses
                </div>
              </div>

              {/* Smart DCA */}
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                }}>
                  <span>Smart DCA</span>
                  <span style={{ fontWeight: '700', color: '#10b981' }}>68%</span>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  height: '12px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                    height: '100%',
                    width: '68%',
                    borderRadius: '8px',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.25rem' }}>
                  54 wins / 25 losses
                </div>
              </div>

              {/* BTC Rebalancer */}
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                }}>
                  <span>BTC Rebalancer</span>
                  <span style={{ fontWeight: '700', color: '#f59e0b' }}>65%</span>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  height: '12px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)',
                    height: '100%',
                    width: '65%',
                    borderRadius: '8px',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.25rem' }}>
                  42 wins / 23 losses
                </div>
              </div>

            </div>

            {/* Overall */}
            <div style={{
              marginTop: '2rem',
              padding: '1rem',
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '10px',
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '0.95rem' }}>Overall Win Rate</span>
                <span style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#3b82f6',
                }}>68.5%</span>
              </div>
            </div>
          </div>

        </div>

        {/* ============================================ */}
        {/* Bot Win Rate Performance - Historical Chart */}
        {/* ============================================ */}

        <div style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.1)',
          marginTop: '3rem',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <span style={{ fontSize: '24px' }}>📈</span>
            <div>
              <h2 style={{ fontSize: '1.8rem', marginBottom: '0.25rem', fontWeight: '700' }}>
                Bot Win Rate Performance
              </h2>
              <p style={{ opacity: 0.7, fontSize: '0.95rem', margin: 0 }}>
                Track your bot's historical performance and trading accuracy over time
              </p>
            </div>
          </div>

          <WinRateChart days={30} />
        </div>

        {/* ============================================ */}
        {/* Social Trading - Phase B.9 */}
        {/* ============================================ */}

        <div style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.1)',
          marginTop: '3rem',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
            flexWrap: 'wrap',
            gap: '1rem',
          }}>
            <div>
              <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', fontWeight: '700' }}>
                👥 Community Strategies
              </h2>
              <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                Copy successful strategies from top traders
              </p>
            </div>
            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '10px',
              padding: '0.5rem 1rem',
              fontSize: '0.85rem',
              fontWeight: '600',
              color: '#8b5cf6',
            }}>
              🔥 Trending Now
            </div>
          </div>

          {/* Strategy Cards Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
          }}>

            {/* Strategy 1 - Top Performer */}
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid rgba(255,255,255,0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}>
              {/* Top Badge */}
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                padding: '0.25rem 0.75rem',
                borderRadius: '8px',
                fontSize: '0.75rem',
                fontWeight: '700',
                color: '#fff',
              }}>
                🏆 TOP
              </div>

              {/* Trader Info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem',
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                }}>
                  A
                </div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>AlphaTrader</div>
                  <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>347 followers</div>
                </div>
              </div>

              {/* Strategy Name */}
              <div style={{
                fontSize: '1.2rem',
                fontWeight: '700',
                marginBottom: '0.75rem',
              }}>
                Aggressive Grid Bot
              </div>

              <div style={{
                opacity: 0.8,
                fontSize: '0.9rem',
                marginBottom: '1.25rem',
                lineHeight: '1.5',
              }}>
                High-frequency Infinity Grid on volatile pairs. Best for bull markets.
              </div>

              {/* Performance Metrics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem',
                marginBottom: '1.25rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
              }}>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>30d Return</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>+52%</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Win Rate</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#3b82f6' }}>75%</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Followers</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>347</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Risk</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#f59e0b' }}>High</div>
                </div>
              </div>

              {/* Copy Button */}
              <button
                onClick={() => toast.info('Copy strategy... (Phase B.9 - UI Only)')}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  border: 'none',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                }}
              >
                📋 Copy Strategy
              </button>
            </div>

            {/* Strategy 2 - Conservative */}
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              {/* Trader Info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem',
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                }}>
                  S
                </div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>SafeStacker</div>
                  <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>198 followers</div>
                </div>
              </div>

              {/* Strategy Name */}
              <div style={{
                fontSize: '1.2rem',
                fontWeight: '700',
                marginBottom: '0.75rem',
              }}>
                Conservative DCA
              </div>

              <div style={{
                opacity: 0.8,
                fontSize: '0.9rem',
                marginBottom: '1.25rem',
                lineHeight: '1.5',
              }}>
                Steady weekly DCA with 40% reserve fund. Low risk, stable returns.
              </div>

              {/* Performance Metrics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem',
                marginBottom: '1.25rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
              }}>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>30d Return</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>+18%</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Win Rate</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#3b82f6' }}>82%</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Followers</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>198</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Risk</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>Low</div>
                </div>
              </div>

              {/* Copy Button */}
              <button
                onClick={() => toast.info('Copy strategy... (Phase B.9 - UI Only)')}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                }}
              >
                📋 Copy Strategy
              </button>
            </div>

            {/* Strategy 3 - BTC Accumulation */}
            <div style={{
              background: 'rgba(255,255,255,0.04)',
              borderRadius: '12px',
              padding: '1.5rem',
              border: '1px solid rgba(255,255,255,0.1)',
            }}>
              {/* Trader Info */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1rem',
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: '700',
                }}>
                  B
                </div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '1.1rem' }}>BTCMaxi</div>
                  <div style={{ opacity: 0.6, fontSize: '0.85rem' }}>421 followers</div>
                </div>
              </div>

              {/* Strategy Name */}
              <div style={{
                fontSize: '1.2rem',
                fontWeight: '700',
                marginBottom: '0.75rem',
              }}>
                ETH → BTC Rebalancer
              </div>

              <div style={{
                opacity: 0.8,
                fontSize: '0.9rem',
                marginBottom: '1.25rem',
                lineHeight: '1.5',
              }}>
                Auto-rebalance ETH/BTC pair to accumulate more Bitcoin over time.
              </div>

              {/* Performance Metrics */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.75rem',
                marginBottom: '1.25rem',
                padding: '1rem',
                background: 'rgba(255,255,255,0.05)',
                borderRadius: '8px',
              }}>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>30d Return</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>+28%</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Win Rate</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#3b82f6' }}>71%</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Followers</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700' }}>421</div>
                </div>
                <div>
                  <div style={{ opacity: 0.6, fontSize: '0.75rem' }}>Risk</div>
                  <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>Low</div>
                </div>
              </div>

              {/* Copy Button */}
              <button
                onClick={() => toast.info('Copy strategy... (Phase B.9 - UI Only)')}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                }}
              >
                📋 Copy Strategy
              </button>
            </div>

          </div>

          {/* Browse More Button */}
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <button
              style={{
                padding: '0.875rem 2rem',
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: '10px',
                color: '#fff',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
              }}
            >
              View All Strategies →
            </button>
          </div>
        </div>

        {/* ============================================ */}
        {/* Strategy Performance Comparison + Risk Settings - Phase B.10 */}
        {/* ============================================ */}

        {/* Performance Comparison Chart */}
        <div style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.1)',
          marginTop: '3rem',
        }}>
          <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', fontWeight: '700' }}>
            📊 Strategy Performance Comparison
          </h2>
          <p style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '2rem' }}>
            Compare the performance of all strategies over the last 30 days
          </p>

          {/* Comparison Chart */}
          <div style={{
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '12px',
            padding: '2rem',
            marginBottom: '2rem',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '6px',
              height: '200px',
              marginBottom: '1rem',
            }}>
              {/* Generate comparative data for 3 strategies */}
              {Array.from({ length: 30 }).map((_, idx) => {
                // Infinity Grid (blue) - most volatile, highest peaks
                const infinityGrid = 50 + Math.sin(idx * 0.3) * 25 + idx * 1.8;
                // Smart DCA (green) - steady growth
                const smartDCA = 50 + idx * 1.2 + Math.sin(idx * 0.2) * 10;
                // BTC Rebalancer (orange) - moderate growth
                const btcRebalancer = 50 + idx * 1.5 + Math.sin(idx * 0.25) * 15;

                return (
                  <div key={idx} style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    alignItems: 'center',
                  }}>
                    {/* Infinity Grid bar */}
                    <div
                      style={{
                        width: '100%',
                        background: 'linear-gradient(to top, #3b82f6 0%, #60a5fa 100%)',
                        borderRadius: '2px',
                        height: `${Math.min(infinityGrid, 100)}%`,
                        opacity: 0.7,
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Second row - Smart DCA */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '6px',
              height: '200px',
              marginBottom: '1rem',
            }}>
              {Array.from({ length: 30 }).map((_, idx) => {
                const smartDCA = 50 + idx * 1.2 + Math.sin(idx * 0.2) * 10;
                return (
                  <div key={idx} style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    alignItems: 'center',
                  }}>
                    {/* Smart DCA bar */}
                    <div
                      style={{
                        width: '100%',
                        background: 'linear-gradient(to top, #10b981 0%, #34d399 100%)',
                        borderRadius: '2px',
                        height: `${Math.min(smartDCA, 100)}%`,
                        opacity: 0.7,
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Third row - BTC Rebalancer */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '6px',
              height: '200px',
              marginBottom: '1rem',
            }}>
              {Array.from({ length: 30 }).map((_, idx) => {
                const btcRebalancer = 50 + idx * 1.5 + Math.sin(idx * 0.25) * 15;
                return (
                  <div key={idx} style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                    alignItems: 'center',
                  }}>
                    {/* BTC Rebalancer bar */}
                    <div
                      style={{
                        width: '100%',
                        background: 'linear-gradient(to top, #f59e0b 0%, #fbbf24 100%)',
                        borderRadius: '2px',
                        height: `${Math.min(btcRebalancer, 100)}%`,
                        opacity: 0.7,
                        transition: 'opacity 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0.7';
                      }}
                    />
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '2rem',
              flexWrap: 'wrap',
              marginTop: '1.5rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: '20px',
                  height: '12px',
                  background: 'linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)',
                  borderRadius: '2px',
                }} />
                <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>Infinity Grid (+54%)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: '20px',
                  height: '12px',
                  background: 'linear-gradient(90deg, #10b981 0%, #34d399 100%)',
                  borderRadius: '2px',
                }} />
                <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>Smart DCA (+36%)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{
                  width: '20px',
                  height: '12px',
                  background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)',
                  borderRadius: '2px',
                }} />
                <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>BTC Rebalancer (+45%)</span>
              </div>
            </div>
          </div>

          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
          }}>
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '10px',
              padding: '1.25rem',
            }}>
              <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Best Performer</div>
              <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#3b82f6' }}>Infinity Grid</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '0.25rem' }}>+54% (30 days)</div>
            </div>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '10px',
              padding: '1.25rem',
            }}>
              <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Most Consistent</div>
              <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#10b981' }}>Smart DCA</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '0.25rem' }}>82% win rate</div>
            </div>
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '10px',
              padding: '1.25rem',
            }}>
              <div style={{ opacity: 0.7, fontSize: '0.85rem', marginBottom: '0.5rem' }}>Lowest Risk</div>
              <div style={{ fontSize: '1.3rem', fontWeight: '700', color: '#f59e0b' }}>BTC Rebalancer</div>
              <div style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '0.25rem' }}>Max DD: -8%</div>
            </div>
          </div>
        </div>

        {/* Global Risk Settings */}
        <div style={{
          background: 'rgba(255,255,255,0.06)',
          backdropFilter: 'blur(20px)',
          borderRadius: '16px',
          padding: '2rem',
          border: '1px solid rgba(255,255,255,0.1)',
          marginTop: '2rem',
        }}>
          <h2 style={{ fontSize: '1.8rem', marginBottom: '0.5rem', fontWeight: '700' }}>
            ⚙️ Global Risk Settings
          </h2>
          <p style={{ opacity: 0.7, fontSize: '0.95rem', marginBottom: '2rem' }}>
            Set portfolio-wide risk limits and emergency controls
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
          }}>
            {/* Max Portfolio Risk */}
            <div>
              <label style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                opacity: 0.9,
              }}>
                <span>Max Portfolio Risk</span>
                <span style={{ color: '#ef4444' }}>30%</span>
              </label>
              <input
                type="range"
                min={10}
                max={100}
                defaultValue={30}
                step={5}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  background: 'rgba(239, 68, 68, 0.3)',
                  outline: 'none',
                }}
              />
              <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.5rem' }}>
                Maximum % of portfolio at risk across all strategies
              </p>
            </div>

            {/* Emergency Stop Loss */}
            <div>
              <label style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                opacity: 0.9,
              }}>
                <span>Emergency Stop Loss</span>
                <span style={{ color: '#ef4444' }}>-20%</span>
              </label>
              <input
                type="range"
                min={5}
                max={50}
                defaultValue={20}
                step={1}
                style={{
                  width: '100%',
                  height: '6px',
                  borderRadius: '3px',
                  background: 'rgba(239, 68, 68, 0.3)',
                  outline: 'none',
                }}
              />
              <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.5rem' }}>
                Auto-close all positions if total loss exceeds this %
              </p>
            </div>

            {/* Daily Loss Limit */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                fontWeight: '600',
                opacity: 0.9,
              }}>
                Daily Loss Limit (USDT)
              </label>
              <input
                type="number"
                defaultValue={500}
                min={50}
                max={10000}
                step={50}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  color: '#fff',
                  fontSize: '1rem',
                }}
              />
              <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.5rem' }}>
                Pause all strategies if daily loss exceeds this amount
              </p>
            </div>
          </div>

          {/* Emergency Controls */}
          <div style={{
            marginTop: '2rem',
            padding: '1.5rem',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '12px',
          }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '1rem', color: '#ef4444' }}>
              🚨 Emergency Controls
            </h3>
            <div style={{
              display: 'flex',
              gap: '1rem',
              flexWrap: 'wrap',
            }}>
              <button
                onClick={() => toast.warning('Pausing all strategies... (Phase B.10 - UI Only)')}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '0.875rem 1.5rem',
                  background: 'rgba(245, 158, 11, 0.2)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  borderRadius: '10px',
                  color: '#f59e0b',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(245, 158, 11, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(245, 158, 11, 0.2)';
                }}
              >
                ⏸️ Pause All Strategies
              </button>
              <button
                onClick={async () => {
                  const confirmed = await confirm({
                    title: 'Fermer toutes les positions',
                    message: 'Êtes-vous sûr de vouloir fermer toutes les positions ? Cette action est irréversible.',
                    confirmText: 'Fermer tout',
                    cancelText: 'Annuler',
                    variant: 'danger'
                  });
                  if (confirmed) {
                    toast.warning('Closing all positions... (Phase B.10 - UI Only)');
                  }
                }}
                style={{
                  flex: 1,
                  minWidth: '200px',
                  padding: '0.875rem 1.5rem',
                  background: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '10px',
                  color: '#ef4444',
                  fontSize: '0.95rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                }}
              >
                🛑 Emergency Stop All
              </button>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{
          background: 'rgba(59, 130, 246, 0.08)',
          border: '1px solid rgba(59, 130, 246, 0.2)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginTop: '2rem',
          marginBottom: '2rem',
        }}>
          <h3 style={{ fontSize: '1rem', fontWeight: '700', marginBottom: '0.75rem', color: '#3b82f6' }}>
            ℹ️ Important Notice
          </h3>
          <p style={{ fontSize: '0.9rem', lineHeight: '1.6', opacity: 0.8 }}>
            This is a Paper Trading simulation using mock data. No real funds are at risk.
            All strategies, positions, and performance metrics are for demonstration purposes only.
            Always test strategies thoroughly in paper trading mode before deploying with real capital.
          </p>
        </div>

      </div>

      {/* ============================================ */}
      {/* Configuration Modal */}
      {/* ============================================ */}

      {showConfigModal && selectedStrategy && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '2rem',
          }}
          onClick={() => setShowConfigModal(false)}
        >
          <div
            style={{
              background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
              borderRadius: '24px',
              padding: '2.5rem',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid rgba(255,255,255,0.2)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Infinity Grid Configuration */}
            {selectedStrategy === StrategyType.INFINITY_GRID && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
                  <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                    Configure Infinity Grid
                  </h2>
                  <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                    Set your parameters for automated grid trading
                  </p>
                </div>

                {/* Trading Pair */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Trading Pair
                  </label>
                  <select
                    aria-label="Trading pair"
                    defaultValue="BTC/USDT"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="BTC/USDT">BTC/USDT</option>
                    <option value="ETH/USDT">ETH/USDT</option>
                    <option value="SOL/USDT">SOL/USDT</option>
                    <option value="BNB/USDT">BNB/USDT</option>
                    <option value="AVAX/USDT">AVAX/USDT</option>
                  </select>
                </div>

                {/* Exchange */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Exchange
                  </label>
                  <select
                    aria-label="Exchange"
                    defaultValue="binance"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="binance">Binance</option>
                    <option value="jupiter">Jupiter (Solana)</option>
                  </select>
                </div>

                {/* Base Amount */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Base Amount (USDT)
                  </label>
                  <input
                    type="number"
                    defaultValue={100}
                    min={10}
                    max={10000}
                    step={10}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Initial position size for each grid level
                  </p>
                </div>

                {/* Take Profit % */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Take Profit</span>
                    <span style={{ color: '#10b981' }}>5%</span>
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={20}
                    defaultValue={5}
                    step={0.5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(16, 185, 129, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>1%</span>
                    <span>20%</span>
                  </div>
                </div>

                {/* Max Positions */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Maximum Positions
                  </label>
                  <input
                    type="number"
                    defaultValue={5}
                    min={1}
                    max={10}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Maximum concurrent grid positions
                  </p>
                </div>

                {/* Dip Levels & Size Multipliers */}
                <div style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem',
                }}>
                  <h3 style={{
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    marginBottom: '1rem',
                    opacity: 0.9,
                  }}>
                    Grid Configuration
                  </h3>
                  <div style={{ fontSize: '0.85rem', lineHeight: '1.8', opacity: 0.8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip Level -5%:</span>
                      <span style={{ fontWeight: '600' }}>1x ($100)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip Level -10%:</span>
                      <span style={{ fontWeight: '600' }}>1.5x ($150)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip Level -15%:</span>
                      <span style={{ fontWeight: '600' }}>2.5x ($250)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip Level -20%:</span>
                      <span style={{ fontWeight: '600' }}>4x ($400)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Dip Level -30%:</span>
                      <span style={{ fontWeight: '600' }}>6x ($600)</span>
                    </div>
                  </div>
                </div>

                {/* Stop Loss % */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Stop Loss</span>
                    <span style={{ color: '#ef4444' }}>-25%</span>
                  </label>
                  <input
                    type="range"
                    min={5}
                    max={50}
                    defaultValue={25}
                    step={1}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(239, 68, 68, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>-5%</span>
                    <span>-50%</span>
                  </div>
                </div>

                {/* Trailing Stop % */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Trailing Stop</span>
                    <span style={{ color: '#f59e0b' }}>2%</span>
                  </label>
                  <input
                    type="range"
                    min={0.5}
                    max={10}
                    defaultValue={2}
                    step={0.5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(245, 158, 11, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>0.5%</span>
                    <span>10%</span>
                  </div>
                </div>

                {/* Max Exposure % */}
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Max Portfolio Exposure</span>
                    <span style={{ color: '#3b82f6' }}>50%</span>
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    defaultValue={50}
                    step={5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(59, 130, 246, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>10%</span>
                    <span>100%</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '2rem',
                }}>
                  <button
                    onClick={() => setShowConfigModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Save configuration
                      toast.success('Strategy configuration saved! (Phase B.4 - UI Only)');
                      setShowConfigModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                    }}
                  >
                    Save & Start Paper Trading
                  </button>
                </div>
              </>
            )}

            {/* Smart DCA Configuration */}
            {selectedStrategy === StrategyType.SMART_DCA && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>💰</div>
                  <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                    Configure Smart DCA
                  </h2>
                  <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                    Intelligent Dollar Cost Averaging with reserve funds
                  </p>
                </div>

                {/* Trading Pair */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Trading Pair
                  </label>
                  <select
                    aria-label="Trading pair"
                    defaultValue="BTC/USDT"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="BTC/USDT">BTC/USDT</option>
                    <option value="ETH/USDT">ETH/USDT</option>
                    <option value="SOL/USDT">SOL/USDT</option>
                    <option value="BNB/USDT">BNB/USDT</option>
                    <option value="AVAX/USDT">AVAX/USDT</option>
                  </select>
                </div>

                {/* Exchange */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Exchange
                  </label>
                  <select
                    aria-label="Exchange"
                    defaultValue="binance"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="binance">Binance</option>
                    <option value="jupiter">Jupiter (Solana)</option>
                  </select>
                </div>

                {/* Base Amount */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Base Amount (USDT)
                  </label>
                  <input
                    type="number"
                    defaultValue={50}
                    min={10}
                    max={5000}
                    step={10}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Regular DCA buy amount
                  </p>
                </div>

                {/* Frequency */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    DCA Frequency
                  </label>
                  <select
                    aria-label="DCA Frequency"
                    defaultValue="weekly"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {/* Reserve Fund % */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Reserve Fund</span>
                    <span style={{ color: '#10b981' }}>30%</span>
                  </label>
                  <input
                    type="range"
                    min={10}
                    max={70}
                    defaultValue={30}
                    step={5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(16, 185, 129, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>10%</span>
                    <span>70%</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Funds kept for extreme dip opportunities
                  </p>
                </div>

                {/* Dip Multipliers Configuration */}
                <div style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem',
                }}>
                  <h3 style={{
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    marginBottom: '1rem',
                    opacity: 0.9,
                  }}>
                    Dip Multipliers
                  </h3>
                  <div style={{ fontSize: '0.85rem', lineHeight: '1.8', opacity: 0.8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip -5%:</span>
                      <span style={{ fontWeight: '600' }}>1.5x ($75)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip -10%:</span>
                      <span style={{ fontWeight: '600' }}>2x ($100)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip -15%:</span>
                      <span style={{ fontWeight: '600' }}>3x ($150)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span>Dip -20%:</span>
                      <span style={{ fontWeight: '600' }}>4x ($200)</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Dip -30%:</span>
                      <span style={{ fontWeight: '600' }}>6x ($300)</span>
                    </div>
                  </div>
                </div>

                {/* RSI Pause Threshold */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Pause on RSI Above</span>
                    <span style={{ color: '#f59e0b' }}>75</span>
                  </label>
                  <input
                    type="range"
                    min={60}
                    max={85}
                    defaultValue={75}
                    step={1}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(245, 158, 11, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>60</span>
                    <span>85</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Pause DCA when RSI indicates overbought conditions
                  </p>
                </div>

                {/* MA200 Boost (SmartDCA Enhanced) */}
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                  }}>
                    <h3 style={{
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      margin: 0,
                      color: '#8b5cf6',
                    }}>
                      📊 MA200 Boost (Enhanced)
                    </h3>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                    }}>
                      <input type="checkbox" style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <span>Enable</span>
                    </label>
                  </div>

                  <p style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '1rem', lineHeight: '1.5' }}>
                    Boost your DCA purchases when price is near the 200-period moving average.
                    Combines scheduled DCA with MA200 value zone buying.
                  </p>

                  {/* MA200 Timeframe */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      MA200 Timeframe
                    </label>
                    <select
                      aria-label="MA200 Timeframe"
                      defaultValue="1d"
                      disabled
                      style={{
                        width: '100%',
                        padding: '0.65rem 0.85rem',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.9rem',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    >
                      <option value="1h">1 Hour</option>
                      <option value="4h">4 Hours</option>
                      <option value="1d">1 Day (Recommended)</option>
                      <option value="1w">1 Week</option>
                    </select>
                  </div>

                  {/* MA Type */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      MA Type
                    </label>
                    <select
                      aria-label="MA Type"
                      defaultValue="false"
                      disabled
                      style={{
                        width: '100%',
                        padding: '0.65rem 0.85rem',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: '8px',
                        color: '#fff',
                        fontSize: '0.9rem',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    >
                      <option value="false">SMA200 (Simple)</option>
                      <option value="true">EMA200 (Exponential)</option>
                    </select>
                  </div>

                  {/* MA200 Threshold */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      <span>MA200 Trigger Zone</span>
                      <span style={{ color: '#8b5cf6' }}>±5%</span>
                    </label>
                    <input
                      type="range"
                      min={2}
                      max={10}
                      defaultValue={5}
                      step={0.5}
                      disabled
                      style={{
                        width: '100%',
                        height: '5px',
                        borderRadius: '3px',
                        background: 'rgba(139, 92, 246, 0.2)',
                        outline: 'none',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.7rem',
                      opacity: 0.5,
                      marginTop: '0.25rem',
                    }}>
                      <span>±2%</span>
                      <span>±10%</span>
                    </div>
                  </div>

                  {/* MA200 Boost Multiplier */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      <span>Near MA200 Boost</span>
                      <span style={{ color: '#3b82f6' }}>1.5x</span>
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      defaultValue={1.5}
                      step={0.1}
                      disabled
                      style={{
                        width: '100%',
                        height: '5px',
                        borderRadius: '3px',
                        background: 'rgba(59, 130, 246, 0.2)',
                        outline: 'none',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.7rem',
                      opacity: 0.5,
                      marginTop: '0.25rem',
                    }}>
                      <span>1x</span>
                      <span>3x</span>
                    </div>
                  </div>

                  {/* Below MA200 Extra Boost */}
                  <div style={{ marginBottom: '0' }}>
                    <label style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      <span>Below MA200 Extra Boost</span>
                      <span style={{ color: '#10b981' }}>2.0x</span>
                    </label>
                    <input
                      type="range"
                      min={1}
                      max={3}
                      defaultValue={2}
                      step={0.1}
                      disabled
                      style={{
                        width: '100%',
                        height: '5px',
                        borderRadius: '3px',
                        background: 'rgba(16, 185, 129, 0.2)',
                        outline: 'none',
                        cursor: 'not-allowed',
                        opacity: 0.6,
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.7rem',
                      opacity: 0.5,
                      marginTop: '0.25rem',
                    }}>
                      <span>1x</span>
                      <span>3x</span>
                    </div>
                    <p style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.5rem', marginBottom: 0 }}>
                      Extra boost when price is BELOW MA200 (value zone)
                    </p>
                  </div>
                </div>

                {/* Max Position Size */}
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Max Position Size (USDT)
                  </label>
                  <input
                    type="number"
                    defaultValue={5000}
                    min={100}
                    max={50000}
                    step={100}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Maximum total position size for this strategy
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '2rem',
                }}>
                  <button
                    onClick={() => setShowConfigModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Save configuration
                      toast.success('Smart DCA configuration saved! (Phase B.5 - UI Only)');
                      setShowConfigModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }}
                  >
                    Save & Start Paper Trading
                  </button>
                </div>
              </>
            )}

            {/* BTC Rebalancer Configuration */}
            {selectedStrategy === StrategyType.BTC_REBALANCER && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>₿</div>
                  <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                    Configure BTC Rebalancer
                  </h2>
                  <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                    Automatically accumulate BTC by rebalancing ALT/BTC pairs
                  </p>
                </div>

                {/* ALT Token */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    ALT Token
                  </label>
                  <select
                    aria-label="ALT Token"
                    defaultValue="ETH"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="ETH">ETH (Ethereum)</option>
                    <option value="SOL">SOL (Solana)</option>
                    <option value="AVAX">AVAX (Avalanche)</option>
                    <option value="BNB">BNB (Binance Coin)</option>
                    <option value="MATIC">MATIC (Polygon)</option>
                  </select>
                </div>

                {/* Exchange */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Exchange
                  </label>
                  <select
                    aria-label="Exchange"
                    defaultValue="binance"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="binance">Binance</option>
                    <option value="jupiter">Jupiter (Solana)</option>
                  </select>
                </div>

                {/* Target Ratio */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Target BTC Ratio</span>
                    <span style={{ color: '#f59e0b' }}>50%</span>
                  </label>
                  <input
                    type="range"
                    min={30}
                    max={70}
                    defaultValue={50}
                    step={5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(245, 158, 11, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>30% BTC</span>
                    <span>70% BTC</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Desired portfolio balance between ALT and BTC
                  </p>
                </div>

                {/* Rebalance Thresholds */}
                <div style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem',
                }}>
                  <h3 style={{
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    marginBottom: '1rem',
                    opacity: 0.9,
                  }}>
                    Rebalance Thresholds
                  </h3>

                  {/* High Threshold */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      opacity: 0.8,
                    }}>
                      <span>ALT Outperformance</span>
                      <span style={{ color: '#10b981' }}>+15%</span>
                    </label>
                    <input
                      type="range"
                      min={5}
                      max={30}
                      defaultValue={15}
                      step={1}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: 'rgba(16, 185, 129, 0.3)',
                        outline: 'none',
                      }}
                    />
                    <p style={{ fontSize: '0.75rem', opacity: 0.5, marginTop: '0.25rem' }}>
                      Sell ALT when it pumps above this threshold
                    </p>
                  </div>

                  {/* Low Threshold */}
                  <div>
                    <label style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      fontSize: '0.85rem',
                      opacity: 0.8,
                    }}>
                      <span>BTC Outperformance</span>
                      <span style={{ color: '#ef4444' }}>-10%</span>
                    </label>
                    <input
                      type="range"
                      min={5}
                      max={25}
                      defaultValue={10}
                      step={1}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: 'rgba(239, 68, 68, 0.3)',
                        outline: 'none',
                      }}
                    />
                    <p style={{ fontSize: '0.75rem', opacity: 0.5, marginTop: '0.25rem' }}>
                      Buy ALT when BTC outperforms by this threshold
                    </p>
                  </div>
                </div>

                {/* Min Rebalance Amount */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Min Rebalance Amount (USDT)
                  </label>
                  <input
                    type="number"
                    defaultValue={100}
                    min={10}
                    max={1000}
                    step={10}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                    }}
                  />
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Minimum trade size to avoid excessive fees
                  </p>
                </div>

                {/* Min Profit After Fees */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Min Profit After Fees</span>
                    <span style={{ color: '#10b981' }}>2%</span>
                  </label>
                  <input
                    type="range"
                    min={0.5}
                    max={5}
                    defaultValue={2}
                    step={0.5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(16, 185, 129, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>0.5%</span>
                    <span>5%</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Only rebalance if profit exceeds this after fees
                  </p>
                </div>

                {/* Momentum Check Period */}
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Momentum Check Period (hours)
                  </label>
                  <select
                    aria-label="Momentum Check Period (hours)"
                    defaultValue="24"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="12">12 hours</option>
                    <option value="24">24 hours</option>
                    <option value="48">48 hours</option>
                    <option value="72">72 hours</option>
                  </select>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                    Period to check price momentum before rebalancing
                  </p>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '2rem',
                }}>
                  <button
                    onClick={() => setShowConfigModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Save configuration
                      toast.success('BTC Rebalancer configuration saved! (Phase B.5 - UI Only)');
                      setShowConfigModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(245, 158, 11, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.3)';
                    }}
                  >
                    Save & Start Paper Trading
                  </button>
                </div>
              </>
            )}

            {/* MA200 DCA Configuration */}
            {selectedStrategy === StrategyType.MA200_DCA && (
              <>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📈</div>
                  <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                    Configure MA200 DCA
                  </h2>
                  <p style={{ opacity: 0.7, fontSize: '0.95rem' }}>
                    Value investing strategy - Buy only when price is near MA200
                  </p>
                </div>

                {/* Strategy Name */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Strategy Name
                  </label>
                  <input
                    type="text"
                    defaultValue="My MA200 DCA Strategy"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                    }}
                  />
                </div>

                {/* Trading Pair */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Trading Pair
                  </label>
                  <select
                    aria-label="Trading pair"
                    defaultValue="BTC/USDT"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="BTC/USDT">BTC/USDT</option>
                    <option value="ETH/USDT">ETH/USDT</option>
                    <option value="HBAR/USDT">HBAR/USDT</option>
                    <option value="SOL/USDT">SOL/USDT</option>
                    <option value="AVAX/USDT">AVAX/USDT</option>
                  </select>
                </div>

                {/* Exchange */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Exchange
                  </label>
                  <select
                    aria-label="Exchange"
                    defaultValue="binance"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="binance">Binance</option>
                    <option value="cryptocom">Crypto.com (Coming Q1 2025)</option>
                  </select>
                </div>

                {/* MA200 Settings Section */}
                <div style={{
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                }}>
                  <h3 style={{
                    fontSize: '1rem',
                    fontWeight: '600',
                    marginBottom: '1.25rem',
                    color: '#8b5cf6',
                  }}>
                    📊 MA200 Settings
                  </h3>

                  {/* Timeframe */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      Timeframe
                    </label>
                    <select
                      aria-label="Timeframe"
                      defaultValue="1d"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="1h">1 Hour</option>
                      <option value="4h">4 Hours</option>
                      <option value="1d">1 Day (Recommended)</option>
                      <option value="1w">1 Week</option>
                    </select>
                  </div>

                  {/* MA Type */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      Moving Average Type
                    </label>
                    <select
                      aria-label="Moving Average Type"
                      defaultValue="false"
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem',
                        cursor: 'pointer',
                      }}
                    >
                      <option value="false">SMA200 (Simple)</option>
                      <option value="true">EMA200 (Exponential)</option>
                    </select>
                  </div>

                  {/* MA200 Threshold */}
                  <div style={{ marginBottom: '0' }}>
                    <label style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      <span>MA200 Trigger Zone</span>
                      <span style={{ color: '#8b5cf6' }}>±5%</span>
                    </label>
                    <input
                      type="range"
                      min={2}
                      max={10}
                      defaultValue={5}
                      step={0.5}
                      style={{
                        width: '100%',
                        height: '6px',
                        borderRadius: '3px',
                        background: 'rgba(139, 92, 246, 0.3)',
                        outline: 'none',
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: '0.75rem',
                      opacity: 0.5,
                      marginTop: '0.25rem',
                    }}>
                      <span>±2%</span>
                      <span>±10%</span>
                    </div>
                    <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.5rem' }}>
                      Buy when price is within this % range of MA200
                    </p>
                  </div>
                </div>

                {/* Budget Management */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    Total Budget (USDT)
                  </label>
                  <input
                    type="number"
                    defaultValue={5000}
                    min={100}
                    max={100000}
                    step={100}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                    }}
                  />
                </div>

                {/* Min/Max Purchase Amount */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      Min Purchase (USDT)
                    </label>
                    <input
                      type="number"
                      defaultValue={50}
                      min={10}
                      max={500}
                      step={10}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      Max Purchase (USDT)
                    </label>
                    <input
                      type="number"
                      defaultValue={1000}
                      min={100}
                      max={10000}
                      step={100}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                </div>

                {/* Aggressiveness Multiplier */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Aggressiveness Multiplier</span>
                    <span style={{ color: '#3b82f6' }}>2.0x</span>
                  </label>
                  <input
                    type="range"
                    min={0.5}
                    max={5}
                    defaultValue={2}
                    step={0.5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(59, 130, 246, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>0.5x (Conservative)</span>
                    <span>5x (Aggressive)</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.5rem' }}>
                    Base multiplier for purchase amounts based on distance from MA200
                  </p>
                </div>

                {/* Below MA200 Boost */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Below MA200 Boost</span>
                    <span style={{ color: '#10b981' }}>1.5x</span>
                  </label>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    defaultValue={1.5}
                    step={0.1}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(16, 185, 129, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>1x (No Boost)</span>
                    <span>3x (Max Boost)</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.5rem' }}>
                    Additional boost when price is BELOW MA200 (value zone)
                  </p>
                </div>

                {/* Reserve Fund */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    opacity: 0.9,
                  }}>
                    <span>Reserve Fund</span>
                    <span style={{ color: '#f59e0b' }}>20%</span>
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={50}
                    defaultValue={20}
                    step={5}
                    style={{
                      width: '100%',
                      height: '6px',
                      borderRadius: '3px',
                      background: 'rgba(245, 158, 11, 0.3)',
                      outline: 'none',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '0.75rem',
                    opacity: 0.5,
                    marginTop: '0.25rem',
                  }}>
                    <span>0%</span>
                    <span>50%</span>
                  </div>
                  <p style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.5rem' }}>
                    Percentage of budget to keep in reserve for extreme opportunities
                  </p>
                </div>

                {/* Safety Limits */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      Max Daily Purchases
                    </label>
                    <input
                      type="number"
                      defaultValue={3}
                      min={1}
                      max={10}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      opacity: 0.9,
                    }}>
                      Max Weekly Purchases
                    </label>
                    <input
                      type="number"
                      defaultValue={10}
                      min={1}
                      max={50}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '1rem',
                      }}
                    />
                  </div>
                </div>

                {/* Advanced Options */}
                <div style={{
                  background: 'rgba(255,255,255,0.05)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem',
                }}>
                  <h3 style={{
                    fontSize: '0.95rem',
                    fontWeight: '600',
                    marginBottom: '1rem',
                    opacity: 0.9,
                  }}>
                    ⚙️ Advanced Options
                  </h3>

                  {/* Pause Above MA200 */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}>
                      <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <span>Pause when price is ABOVE MA200</span>
                    </label>
                    <p style={{ fontSize: '0.8rem', opacity: 0.6, marginLeft: '1.8rem', marginTop: '0.25rem' }}>
                      Only buy when in value zone (below MA200)
                    </p>
                  </div>

                  {/* Combine with RSI */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}>
                      <input type="checkbox" style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <span>Combine with RSI filter</span>
                    </label>
                    <p style={{ fontSize: '0.8rem', opacity: 0.6, marginLeft: '1.8rem', marginTop: '0.25rem' }}>
                      Only buy when RSI &lt; 40 (additional safety)
                    </p>
                  </div>

                  {/* Dry Run */}
                  <div>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                    }}>
                      <input type="checkbox" defaultChecked style={{ width: '18px', height: '18px', cursor: 'pointer' }} />
                      <span style={{ fontWeight: '600', color: '#f59e0b' }}>Dry Run Mode (Paper Trading)</span>
                    </label>
                    <p style={{ fontSize: '0.8rem', opacity: 0.6, marginLeft: '1.8rem', marginTop: '0.25rem' }}>
                      Test strategy without real money
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  marginTop: '2rem',
                }}>
                  <button
                    onClick={() => setShowConfigModal(false)}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'rgba(255,255,255,0.08)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // TODO: Save configuration
                      toast.success('MA200 DCA configuration saved! (Phase B.5 - UI Only)');
                      setShowConfigModal(false);
                    }}
                    style={{
                      flex: 1,
                      padding: '0.875rem 1.5rem',
                      background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                      border: 'none',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.3)';
                    }}
                  >
                    Save & Start Paper Trading
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Binance Connection Modal */}
      <BinanceConnectionModal
        isOpen={showBinanceModal}
        onClose={() => setShowBinanceModal(false)}
        onConnectionSuccess={handleBinanceConnectionSuccess}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal {...confirmationProps} />

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default Autopilot;
